import { LitElement, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { Modal } from 'bootstrap';

import '../gi-post/gi-post';
import { Post } from '../bulletin-board.types';

// Helpers
export function sortPostsByWhenPosted(a: Post, b: Post) {
  const aDate = new Date(a.whenPosted);
  const bDate = new Date(b.whenPosted);

  if (aDate < bDate) {
    return 1;
  } else if (aDate > bDate) {
    return -1;
  }

  return 0;
}

@customElement('general-information')
export class GeneralInformation extends LitElement {
  static MODAL_TITLE = 'Delete General Information Post';
  static MODAL_WARNING =
    'Deleting a post will permanently remove it from provider and supplier views of the General Information channel.';
  static MODAL_ACKNOWLEDGEMENT =
    'I acknowledge deleting this post will result in all information contained being permanently removed.';

  @property({ type: Boolean })
  currentUserCanDelete = false;

  @property({ type: Array })
  posts: Array<Post> = [];

  @state()
  allowSubmit = false;

  warningModal!: Modal;
  deletePostId: number | null = null;

  sortedPostsMap = new WeakMap<Array<Post>, Array<Post>>();

  @query('#acknowledgement') _acknowledgement!: HTMLInputElement;

  protected override createRenderRoot() {
    return this;
  }

  renderWarningModal(title: string, warning: string, acknowledgement: string) {
    return html` <!-- Modal -->
      <div
        class="modal fade"
        id="warningModal"
        tabindex="-1"
        aria-labelledby="exampleModalLabel"
        aria-hidden="true"
      >
        <div class="modal-dialog modal-lg">
          <div class="modal-content">
            <div class="modal-header">
              <h1 class="modal-title fs-5" id="exampleModalLabel">${title}</h1>
              <button
                type="button"
                class="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div class="modal-body">
              <div
                class="alert alert-warning d-flex align-items-center"
                role="alert"
              >
                <i class="fa-solid fa-triangle-exclamation"></i>&nbsp;
                <div>${warning}</div>
              </div>
              <input
                class="form-check-input"
                type="checkbox"
                id="acknowledgement"
                @click="${() => (this.allowSubmit = !this.allowSubmit)}"
              />
              <label class="form-check-label" for="acknowledgement">
                ${acknowledgement}
              </label>
            </div>
            <div class="modal-footer">
              <button
                type="button"
                class="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                Cancel
              </button>
              <button
                type="button"
                class="btn btn-primary"
                ?disabled="${!this.allowSubmit}"
                @click="${this.deletePost}"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      </div>`;
  }

  renderPosts(posts: Array<Post>) {
    if (posts?.length === 0) {
      return html`<div class="empty">
        Posts to all registered suppliers will appear here.
      </div>`;
    } else {
      let sortedPosts = this.sortedPostsMap.get(posts);

      if (sortedPosts === undefined) {
        sortedPosts = [...posts].sort(sortPostsByWhenPosted);
        this.sortedPostsMap.set(posts, sortedPosts);
      }

      return html`${sortedPosts.map((post, index) => {
        return html`<gi-post
          .currentUserCanDelete="${this.currentUserCanDelete}"
          .created="${post.whenPosted}"
          .text="${post.text}"
          .attachments="${post.attachments}"
          .postId="${post.id}"
          .last="${index === posts.length - 1}"
          @delete="${this.showWarningModal}"
        ></gi-post>`;
      })}`;
    }
  }

  override render() {
    return html` <style>
        general-information h1 {
          margin-top: 0;
          font-size: 16px;
          font-weight: 500;
          line-height: 19px;
        }

        general-information hr#name-separator {
          display: block;
          height: 1px;
          border: 0;
          border-top: 1px solid var(--green);
          margin: 0.75em 0;
          padding: 0;
        }

        general-information div.empty {
          color: var(--gray-400);
        }
      </style>
      <div>
        <h1>General Information</h1>
        <hr id="name-separator" />
        ${this.renderPosts(this.posts)}
      </div>
      ${this.renderWarningModal(
        GeneralInformation.MODAL_TITLE,
        GeneralInformation.MODAL_WARNING,
        GeneralInformation.MODAL_ACKNOWLEDGEMENT,
      )}`;
  }

  protected override firstUpdated(): void {
    this.warningModal = new Modal('#warningModal', {});
  }

  // Actions
  showWarningModal(event: CustomEvent) {
    event.stopPropagation();
    this.deletePostId = event.detail.giPostId;
    this.warningModal.show();
  }

  deletePost() {
    this.warningModal.hide();

    // Emit an event for the deletion.
    this.dispatchEvent(
      new CustomEvent('delete', {
        bubbles: true,
        composed: true,
        detail: {
          giPostId: this.deletePostId,
        },
      }),
    );

    this._acknowledgement.checked = false;
    this.allowSubmit = false;
  }
}
